"use client";

import { useEffect, useRef, useState } from "react";

import { faPlus, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import PracticeSelector from "@/components/ui/practice-selector";

import Buttons from "../../components/buttons";
import Wizard from "../../components/wizard";
import { useWizard } from "../../context";
import styles from "../index.module.scss";

type Props = {
  [k: string]: unknown;
};

function PartOne({ onNext }: Props & { onNext: () => void }) {
  const { state, actions, questions } = useWizard();

  const ref = useRef<HTMLFormElement | null>(null);

  const [complete, setComplete] = useState(false);

  const answers = state.data.answers;

  const handleOnChange = async (name: string, value: unknown) => {
    const data = {
      ...answers,
      [name]: value,
    };

    actions.getQuestions(data);

    return actions.setData({
      answers: data,
    });
  };

  useEffect(() => {
    setComplete(ref.current?.checkValidity() ?? false);
  }, [questions, answers]);

  return (
    <>
      <p className="text-style-body2-regular mt-2">
        Part 1 of 2: Please enter your pet&apos;s medical history.
      </p>

      <div className={styles.container}>
        <form ref={ref} onSubmit={(e) => e.preventDefault()}>
          <Wizard questions={questions ?? []} answers={answers ?? {}} onChange={handleOnChange} />
          <Buttons disabled={!complete} onNext={onNext} />
        </form>
      </div>
    </>
  );
}

function PartTwo({ onBack }: Props & { onBack: () => void }) {
  const { state, actions, questions } = useWizard();
  const [mode, setMode] = useState("");

  const pet = state.data.pet;
  const practices = pet?.practices ?? [];

  const renderContent = () => {
    if (mode === "new") {
      return (
        <div className={styles.container}>
          <PracticeSelector
            search
            enableGeo={true}
            selected={state.data.trialPractice}
            onSelect={({ id, name }) => {
              if (pet) {
                actions.setData({
                  pet: {
                    ...pet,
                    practices: [...practices, { id, name }],
                  },
                });
              }
              setMode("");
            }}
          />
        </div>
      );
    }

    return (
      <div className="cursor--pointer ms-3 my-3 ps-1" onClick={() => setMode("new")}>
        <FontAwesomeIcon icon={faPlus} color="#5C5F62" className="me-3" />
        Add {practices.length > 0 ? "another" : ""} clinic
      </div>
    );
  };

  return (
    <>
      <p className="text-style-body2-regular mt-2">
        {questions.length ? "Part 2 of 2:" : ""} Please confirm which practice(s) your pet is
        registered with.
      </p>

      <ol>
        {practices?.map(({ id, name }, index) => (
          <li
            key={index}
            className="cursor--pointer my-3"
            onClick={() => {
              if (pet) {
                actions.setData({
                  pet: {
                    ...pet,
                    practices: practices.filter((p) => p.id !== id),
                  },
                });
              }
            }}
          >
            {name}
            <FontAwesomeIcon icon={faTimes} className="mx-2" color="#C9CCCF" />
          </li>
        ))}
      </ol>

      {renderContent()}

      <Buttons onBack={onBack} text={practices.length > 0 ? "Next" : "Skip"} />
    </>
  );
}

export default function StepThree({}: Props) {
  const { actions, questions } = useWizard();

  actions.goBackCheck(3);

  const [part, setPart] = useState(questions.length ? 1 : 2);

  const handleStep = (part: number) => {
    setPart(part);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
  };

  if (part === 1) {
    return <PartOne onNext={() => handleStep(2)} />;
  }

  return (
    <PartTwo
      onBack={() => {
        if (questions.length > 0) {
          handleStep(1);
        } else {
          actions.prev();
        }
      }}
    />
  );
}
